/**
 * Dashboard page. Built to match `design/Syntra/dashboard.html`:
 *
 *   - Page header (title + dynamic subtitle + "Find deals" CTA).
 *   - 4-tile KPI row (Portfolio Value · Total Equity · Monthly Cash Flow · Pipeline Deals).
 *   - Acquisition pipeline (composition bar + stage chips) beside Portfolio mix (donut).
 *   - Rehab status (summary + active-rehab progress list).
 *   - Portfolio table (per-property cash flow, monthly payment, next EMI date).
 *
 * Numbers come from the same engines as the Properties / Financing / Rehab
 * pages (`computePropertyMetrics`, `computeOwnedLoanInfo`, rehab derivations),
 * so every figure agrees across the app.
 */
import { useMemo } from "react";

import { Loader2, Search } from "lucide-react";
import { Link } from "react-router-dom";

import { AcquisitionPipelineCard } from "@/components/dashboard/AcquisitionPipelineCard";
import {
  aggregateDashboardKpis,
  buildFunnel,
  buildPortfolioMix,
  buildPortfolioRow,
  decorate,
} from "@/components/dashboard/dashboard-metrics";
import { DashboardEmptyState } from "@/components/dashboard/DashboardEmptyState";
import { DashboardKpiRow } from "@/components/dashboard/DashboardKpiRow";
import { PortfolioMixCard } from "@/components/dashboard/PortfolioMixCard";
import { PortfolioTableCard } from "@/components/dashboard/PortfolioTableCard";
import {
  RehabStatusCard,
  type ActiveRehab,
} from "@/components/dashboard/RehabStatusCard";
import {
  deriveBudget,
  deriveProgressPct,
} from "@/components/rehab/rehab-status";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useOwnedProperties } from "@/hooks/use-owned-properties";
import { usePipeline } from "@/hooks/use-pipeline";
import { useRehab } from "@/hooks/use-rehab";
import { useWorkspace } from "@/hooks/use-workspace";

export default function Dashboard() {
  const { isLoading: workspaceLoading } = useWorkspace();
  const { items, isLoading: ownedLoading } = useOwnedProperties();
  const { projects, isLoading: rehabLoading } = useRehab();
  const {
    pipelines,
    getPropertiesByPipeline,
    isLoading: pipelineLoading,
  } = usePipeline();

  const decorated = useMemo(() => items.map(decorate), [items]);

  const kpis = useMemo(
    () =>
      aggregateDashboardKpis(
        decorated.map(({ item, metrics }) => ({ metrics, status: item.status })),
      ),
    [decorated],
  );

  const mix = useMemo(() => buildPortfolioMix(decorated), [decorated]);

  const funnel = useMemo(
    () => buildFunnel(pipelines, getPropertiesByPipeline),
    [pipelines, getPropertiesByPipeline],
  );

  const rows = useMemo(() => decorated.map(buildPortfolioRow), [decorated]);

  const rehab = useMemo(() => {
    let inProgress = 0;
    let completed = 0;
    let budgetDeployed = 0;
    const active: ActiveRehab[] = [];
    for (const p of projects) {
      if (p.stage === "in_progress") {
        inProgress += 1;
        active.push({
          id: p.id,
          name: p.property_address ?? "Property",
          pct: deriveProgressPct(p),
        });
      }
      if (p.stage === "completed") completed += 1;
      budgetDeployed += deriveBudget(p).used;
    }
    return { inProgress, completed, budgetDeployed, active };
  }, [projects]);

  if (workspaceLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isLoading = ownedLoading || rehabLoading || pipelineLoading;

  const ownedCount = items.length;
  const propWord = ownedCount === 1 ? "property" : "properties";
  const dealWord = funnel.totalActive === 1 ? "deal" : "deals";

  // A fresh workspace: nothing owned and nothing in the pipeline. Show the
  // onboarding empty state (design/Syntra/dashboard-empty.html) instead of the
  // full widget grid rendered with zeros.
  const isEmpty = !isLoading && ownedCount === 0 && funnel.totalActive === 0;

  return (
    <div className="flex flex-col gap-3.5">
      <div className="mb-2 flex items-start justify-between gap-6">
        <div>
          <h1 className="mb-1.5 text-[30px] font-extrabold tracking-tight text-foreground">
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEmpty
              ? "Welcome to Syntra — let's get your first property in."
              : `Tracking ${ownedCount} ${propWord} and ${funnel.totalActive} active ${dealWord} in the pipeline.`}
          </p>
        </div>
        <Button asChild className="flex-shrink-0 shadow-[0_4px_14px_hsl(var(--primary)/0.28)]">
          <Link to="/deals">
            <Search className="h-[17px] w-[17px]" />
            Find deals
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3.5">
          <Skeleton className="h-[120px] w-full rounded-[10px]" />
          <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1.4fr_1fr]">
            <Skeleton className="h-[220px] w-full rounded-xl" />
            <Skeleton className="h-[220px] w-full rounded-xl" />
          </div>
          <Skeleton className="h-[200px] w-full rounded-xl" />
          <Skeleton className="h-[260px] w-full rounded-xl" />
        </div>
      ) : isEmpty ? (
        <DashboardEmptyState />
      ) : (
        <>
          <DashboardKpiRow
            kpis={kpis}
            ownedCount={ownedCount}
            pipelineCount={funnel.totalActive}
            pipelineVolume={funnel.totalVolume}
          />

          <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1.4fr_1fr]">
            <AcquisitionPipelineCard funnel={funnel} />
            <PortfolioMixCard slices={mix.slices} total={mix.total} />
          </div>

          <RehabStatusCard
            inProgress={rehab.inProgress}
            completed={rehab.completed}
            budgetDeployed={rehab.budgetDeployed}
            active={rehab.active}
          />

          <PortfolioTableCard rows={rows} />
        </>
      )}
    </div>
  );
}
